import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Alert, Button, ButtonGroup, ProgressBar, Spinner } from 'react-bootstrap';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { UrlHeadCheckResult } from '../api/types';
import {
  headCheckColumnConfig,
  type HeadCheckRow,
} from '../config/headCheckColumnConfig';
import { HeadCheckHelpTip } from './HeadCheckHelpTip';
import { INDEXER_AG_GRID_FILL_HEIGHT, IndexerAgGrid } from './IndexerAgGrid';
import { formatFaNumber } from '../lib/utils';

type RowFilter = 'all' | 'ok' | 'failed' | 'pending';

function rowFilterMatch(
  url: string,
  results: Record<string, UrlHeadCheckResult | undefined>,
  filter: RowFilter,
): boolean {
  const result = results[url];
  if (filter === 'all') return true;
  if (filter === 'pending') return result === undefined;
  if (filter === 'ok') return result?.ok === true;
  if (filter === 'failed') {
    return result !== undefined && !result.ok;
  }
  return true;
}

export function UrlHeadCheckModal({
  show,
  running,
  urls,
  results,
  onClose,
}: {
  show: boolean;
  running: boolean;
  urls: string[];
  results: Record<string, UrlHeadCheckResult | undefined>;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<RowFilter>('all');

  useEffect(() => {
    if (show) setFilter('all');
  }, [show, urls]);

  const doneCount = urls.filter((url) => results[url] !== undefined).length;
  const okCount = urls.filter((url) => results[url]?.ok).length;
  const failedCount = doneCount - okCount;
  const pendingCount = urls.length - doneCount;
  const progressPct =
    urls.length > 0
      ? Math.min(100, Math.round((doneCount / urls.length) * 100))
      : 0;
  const isComplete = !running && doneCount === urls.length && urls.length > 0;

  const rows = useMemo((): HeadCheckRow[] => {
    return urls
      .filter((url) => rowFilterMatch(url, results, filter))
      .map((url) => ({
        url,
        result: results[url],
      }));
  }, [urls, results, filter]);

  const columns = useMemo(
    () => headCheckColumnConfig({ running }),
    [running],
  );

  const gridOptions = useMemo(
    () => ({
      getRowId: (params: { data: HeadCheckRow }) => params.data.url,
      rowHeight: 40,
      animateRows: false,
    }),
    [],
  );

  const showFilters = urls.length > 3 && (running ? doneCount > 0 : true);

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      dialogClassName="indexer-grid-modal"
    >
      <Modal.Header closeButton className="bg-body-tertiary border-bottom py-2">
        <Modal.Title as="div" className="fs--1 fw-semibold mb-0 lh-sm w-100">
          <div className="d-flex align-items-center gap-2">
            <span>تست ۲۰۰ — بررسی HEAD</span>
            <HeadCheckHelpTip />
          </div>
          <div className="text-secondary fs--2 fw-normal mt-1">
            {running
              ? 'در حال بررسی پاسخ HTTP هر آدرس…'
              : isComplete
                ? 'بررسی تمام شد — قبل از ارسال، آدرس‌های ناموفق را حذف کنید.'
                : 'آمادهٔ نمایش نتایج'}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-3 d-flex flex-column">
        {urls.length === 0 ? (
          <p className="text-secondary mb-0 text-center py-4">
            آدرسی برای بررسی نیست.
          </p>
        ) : (
          <div className="indexer-fill-grid">
            {(running || isComplete) && (
              <div className="mb-3 indexer-fill-grid-toolbar">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fs--1 text-secondary">
                    {running ? (
                      <span className="d-inline-flex align-items-center gap-1">
                        <Spinner animation="border" size="sm" />
                        {formatFaNumber(doneCount)} از{' '}
                        {formatFaNumber(urls.length)}
                      </span>
                    ) : (
                      'پیشرفت'
                    )}
                  </span>
                  <span className="fs--1 fw-semibold mono">{progressPct}٪</span>
                </div>
                <ProgressBar
                  now={progressPct}
                  variant={
                    isComplete && failedCount > 0 ? 'warning' : 'primary'
                  }
                  striped={running}
                  animated={running}
                  style={{ height: '0.45rem' }}
                />
              </div>
            )}

            <div className="row g-2 mb-3 indexer-fill-grid-toolbar">
              {(
                [
                  ['all', formatFaNumber(urls.length), 'کل'],
                  ['ok', formatFaNumber(okCount), '۲۰۰'],
                  ['failed', formatFaNumber(failedCount), 'خطا'],
                  ['pending', formatFaNumber(pendingCount), 'در انتظار'],
                ] as const
              ).map(([key, value, label]) => (
                <div key={key} className="col-6 col-sm-3">
                  <div className="border rounded-3 px-2 py-2 text-center bg-body-tertiary h-100">
                    <div className="fw-semibold fs-0">{value}</div>
                    <div className="text-secondary fs--2">{label}</div>
                  </div>
                </div>
              ))}
            </div>

            {isComplete && failedCount === 0 && (
              <Alert variant="success" className="py-2 fs--1 mb-3 indexer-fill-grid-toolbar">
                همهٔ آدرس‌ها HTTP 200 برگرداندند — آمادهٔ ارسال.
              </Alert>
            )}

            {isComplete && failedCount > 0 && (
              <Alert variant="warning" className="py-2 fs--1 mb-3 indexer-fill-grid-toolbar">
                {formatFaNumber(failedCount)} آدرس غیر ۲۰۰ یا خطا داشت — قبل از
                ارسال آن‌ها را از لیست حذف کنید.
              </Alert>
            )}

            {showFilters && (
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2 indexer-fill-grid-toolbar">
                <span className="text-secondary fs--2">فیلتر نمایش</span>
                <ButtonGroup size="sm">
                  {(
                    [
                      ['all', 'همه'],
                      ['ok', '۲۰۰'],
                      ['failed', 'خطا'],
                      ...(running ? ([['pending', 'در انتظار']] as const) : []),
                    ] as const
                  ).map(([value, label]) => (
                    <Button
                      key={value}
                      variant={
                        filter === value ? 'secondary' : 'outline-secondary'
                      }
                      onClick={() => setFilter(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
            )}

            {rows.length === 0 ? (
              <p className="text-secondary text-center py-4 mb-0 fs--1">
                آدرسی با این فیلتر نیست.
              </p>
            ) : (
              <div className="indexer-fill-grid-body">
                <IndexerAgGrid
                  tableName="indexer_head_check"
                  columns={columns}
                  data={rows}
                  height={INDEXER_AG_GRID_FILL_HEIGHT}
                  otherOptions={gridOptions}
                />
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-body-tertiary border-top py-2">
        <Button variant="secondary" size="sm" onClick={onClose}>
          {isComplete ? 'بستن' : running ? 'بستن (بررسی ادامه دارد)' : 'بستن'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
